// /** @format */

import { useState, useCallback, useEffect } from "react";
import intl from "react-intl-universal";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useLocation } from "react-router-dom";
import { Intent } from "@blueprintjs/core";
import { Auth } from "aws-amplify";
// import cx from "classnames";

// import { Logo } from "../../../Resources/SVG";
import { Page } from "design-components"; // LangDropDown
import Styles from "./Styles.module.scss";

import { ClientData } from "providers/clientProvider";
import S3Image from "app/utils/S3ImageCP";
import Footer from "design-components/Footer";
import parentLogger from "app/utils/loggerService";
import { AppInput } from "design-components";
import { validateField, showToast } from "app/AppSupport/Utils";
import { Actions } from "redux/app_actions";
import { FunctionType, IStore } from "redux/reducer_types";
import { StringTools } from "app/AppSupport";
import MarkdownContent from "design-components/MarkdownContent";

const logger = parentLogger.child({
    module: "SignUp2",
});
const xtx = (str, key) => intl.get(key || str || "x") || str;

const LOGOHEIGHT = "60px";

function useUrlQuery() {
    return new URLSearchParams(useLocation().search);
}
export interface IValidatedFieldErrors {
    signInEmailOrMobile?: string;
    accessCode?: string;
    firstName?: string;
    lastName?: string;
    signUpEmail?: string;
    mobile?: string;
}

interface IProps {
    signUpFn: FunctionType;
    signInFn: FunctionType;
    handleCodeSubmit: FunctionType;
    clientDisplayData: ClientData;
}

const SignUp: React.FC<IProps> = (props) => {
    const dispatch = useDispatch();
    let query = useUrlQuery();

    const rawQueryEmail = query.get("email");
    // const newSignUp = query.get("newuser") === "1" ? true : false;
    const providedEmail =
        rawQueryEmail !== null ? decodeURIComponent(rawQueryEmail) : null;
    const signInAutomatically = query.get("signInAutomatically");
    const [doAutomaticSignIn, setDoAutomaticSignIn] = useState(
        signInAutomatically === "true"
    );
    const [showAccessCodeInput, setShowAccessCodeInput] = useState(false);
    const [showGuestAccessButton, setShowGuestAccessButton] = useState(false);
    const [mainButtonText, setMainButtonText] = useState("Sign In");
    const [validated, setValidated] = useState(false);
    const [validatedFields, setValidatedFields] = useState<string[]>([]);
    const [validatedFieldErrors, setValidatedFieldErrors] =
        useState<IValidatedFieldErrors>({
            signInEmailOrMobile: undefined,
            accessCode: undefined,
            firstName: undefined,
            lastName: undefined,
            signUpEmail: undefined,
            mobile: undefined,
        });
    const [signInAsGuest, setSignInAsGuest] = useState(false);

    const { clientDisplayData } = props;
    // return <div>stopping at top of sign up</div>;
    logger.debug(setShowAccessCodeInput);
    logger.debug(setShowGuestAccessButton);
    logger.debug(setMainButtonText);

    const loginDetails =
        useSelector((state: IStore) => state.app.loginDetails, shallowEqual) ||
        {};

    const rawCognitoUser = useSelector(
        (state: IStore) => state.app.rawCognitoUser,
        shallowEqual
    );

    const handleValidation = (
        field_name: string,
        fieldValue: string,
        saveError: boolean
    ) => {
        const { error, overrideSaveError } = validateField(
            field_name,
            fieldValue,
            saveError
        );
        if (
            (error !== "none" && (saveError || overrideSaveError)) ||
            error === "none"
        ) {
            setValidatedFieldErrors({
                ...validatedFieldErrors,
                [field_name]: error,
            });
        }
        const loc = validatedFields.indexOf(field_name);
        if (error === "none" && loc === -1) {
            setValidatedFields([...validatedFields, field_name]);
        } else if (error !== "none" && loc !== -1) {
            const newVf = [...validatedFields];
            newVf.splice(loc, 1);
            setValidatedFields(newVf);
        }
    };

    const allRequiredFieldsValid = useCallback(
        (requiredFields) => {
            return requiredFields.every(
                (f) => validatedFields.indexOf(f) !== -1
            );
        },
        [validatedFields]
    );

    const submitSignIn = () => {
        switch (showAccessCodeInput) {
            case false:
                props.signInFn(loginDetails.emailAddress);
                setShowAccessCodeInput(true);
                break;
            default:
                props.handleCodeSubmit(
                    rawCognitoUser,
                    loginDetails.accessCode.split(/\s+/gi).join("")
                );
                break;
        }
        // resetting accessCode so it doesn't show up again after logging out and trying to log in again
        Actions.setLoginDetails({
            emailAddress: loginDetails.emailAddress,
            accessCode: "",
        });
    };

    useEffect(() => {
        let valid = false;
        if (!showAccessCodeInput) {
            valid = allRequiredFieldsValid(["signInEmailOrMobile"]);
        } else {
            valid = allRequiredFieldsValid(["accessCode"]);
        }
        setValidated(valid);
    }, [validatedFields, showAccessCodeInput, allRequiredFieldsValid]);

    useEffect(() => {
        if (
            !showAccessCodeInput &&
            doAutomaticSignIn &&
            !StringTools.stringIsNullOrEmpty(providedEmail) &&
            (StringTools.validateEmailAddress(providedEmail) ||
                !StringTools.validateMobileNumber(providedEmail))
        ) {
            // console.log(`doing automatic sign in with email ${providedEmail}`);
            dispatch(
                Actions.setLoginDetails({
                    emailAddress: providedEmail!,
                    accessCode: "",
                })
            );
            props.signInFn(providedEmail);
            setShowAccessCodeInput(true);
            // console.log(`setting doAutomaticSignIn to false`);
            setDoAutomaticSignIn(false); // only want to do this once
            showToast(
                `${xtx(
                    "No need to type in your email to sign in. Please check",
                    "signin.automatic_1"
                )} ${providedEmail} ${xtx(
                    " for a 6-digit access code in your email.  If you don't see it, be sure to check in your spam or junk folder",
                    "signin.automatic2"
                )}`,
                0,
                Intent.PRIMARY
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAccessCodeInput, doAutomaticSignIn]);

    useEffect(() => {
        // N.B. very odd -- if the console.log statement is commented out, sometimes the entire app will not load in Safari (root will be without any children)
        // console.log(
        //     `in useEffect with signInAutomatically = ${signInAutomatically}`
        // );
        if (signInAutomatically === "true") {
            // console.log(`setting doAutomaticSignIn to true`);
            setDoAutomaticSignIn(true);
        } else {
            // console.log(`setting doAutomaticSignIn to false`);
            setDoAutomaticSignIn(false);
        }
    }, [signInAutomatically]);

    // @ts-ignore
    useEffect(() => {
        if (showAccessCodeInput) {
            const timer = setTimeout(() => {
                logger.debug("Showing guest login button");
                setShowGuestAccessButton(true);
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, [showAccessCodeInput]);

    useEffect(() => {
        logger.debug("running useEffect in LeftSide");
        if (signInAsGuest) {
            const getGuestCredentials = async () => {
                const bob = await Auth.currentCredentials();
                logger.debug(bob);
                dispatch(
                    Actions.saveAuthStatus({
                        authStatus: "signedInAsGuest",
                        isGuest: true,
                    })
                );
            };
            getGuestCredentials();
        }
    }, [signInAsGuest, dispatch]);

    return (
        <Page
            title={xtx("Login", "signup.login")}
            className={Styles.signUpPage}
            useFluidContainer={true}
            showingFooter={true}
            footerHeight={50}
            pageLanguageSource="user" // N.B. there won't be any source other than the default for this page
        >
            {/* <div className={Styles.langDropDown}>
                        <LangDropDown preventUpdateRolePreferences={true} />
                    </div> */}
            <div
                className="grid"
                style={{
                    gridTemplateAreas:
                        "'. pallene . . .' '. login clientinfo . .' '. footer footer footer .'",
                    gridTemplateColumns: "30px 3fr 2fr 1fr 30px",
                    gridTemplateRows: "auto 1fr auto",
                    height: "100vh",
                }}
            >
                <div
                    style={{
                        gridArea: "pallene",
                        padding: "var(--header-footer-spacing)",
                    }}
                >
                    <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
                        Pallene Video
                    </h2>
                </div>
                <div style={{ gridArea: "login" }}>
                    <>
                        <div className="flex flex-col justify-center sm:px-6 lg:px-8">
                            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                                <S3Image
                                    identityId="not_needed_for_public"
                                    level="public"
                                    imgKey={clientDisplayData.logoS3Filename}
                                    theme={{
                                        photoImg: {
                                            height: LOGOHEIGHT,
                                        },
                                    }}
                                    style={{
                                        display: "grid",
                                        justifyContent: "center",
                                    }}
                                />

                                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                                    Sign in with your email address
                                </h2>
                                <p className="mt-2 text-center text-sm text-green-700">
                                    Or use your mobile number if that's how you
                                    were registered
                                </p>
                            </div>

                            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                                    <div className="space-y-6">
                                        <div>
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Email address
                                            </label>
                                            <div className="w-full">
                                                <AppInput
                                                    onChange={(event) => {
                                                        const emailAddress =
                                                            event.target.value.toLowerCase();
                                                        handleValidation(
                                                            "signInEmailOrMobile",
                                                            emailAddress,
                                                            false
                                                        );
                                                        // setLoginDetails({
                                                        //     emailAddress,
                                                        // });
                                                        dispatch(
                                                            Actions.setLoginDetails(
                                                                {
                                                                    emailAddress,
                                                                    accessCode:
                                                                        "",
                                                                }
                                                            )
                                                        );
                                                    }}
                                                    onBlur={(event) =>
                                                        handleValidation(
                                                            "signInEmailOrMobile",
                                                            event.target.value.toLowerCase(),
                                                            true
                                                        )
                                                    }
                                                    placeholder={xtx(
                                                        "Email or mobile number",
                                                        "signup.email_or_mobile"
                                                    )}
                                                    maxLength={256}
                                                    type="email"
                                                    value={
                                                        loginDetails.emailAddress ||
                                                        ""
                                                    }
                                                    className={"w-full"}
                                                    disabled={
                                                        showAccessCodeInput
                                                    }
                                                />

                                                {/* <input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    autoComplete="email"
                                                    required
                                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                                /> */}
                                            </div>
                                        </div>
                                        {showAccessCodeInput ? (
                                            <div>
                                                <label
                                                    htmlFor="password"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Access code from email
                                                </label>
                                                <div className="w-full">
                                                    <AppInput
                                                        onChange={(event) => {
                                                            const accessCode =
                                                                event.target
                                                                    .value;
                                                            handleValidation(
                                                                "accessCode",
                                                                accessCode,
                                                                false
                                                            );
                                                            dispatch(
                                                                Actions.setLoginDetails(
                                                                    {
                                                                        emailAddress:
                                                                            loginDetails.emailAddress ||
                                                                            "",
                                                                        accessCode,
                                                                    }
                                                                )
                                                            );
                                                        }}
                                                        onBlur={(event) =>
                                                            handleValidation(
                                                                "accessCode",
                                                                event.target
                                                                    .value,
                                                                true
                                                            )
                                                        }
                                                        placeholder={xtx(
                                                            "Enter code sent to email or mobile",
                                                            "signup.enter_code"
                                                        )}
                                                        maxLength={16}
                                                        value={
                                                            loginDetails.accessCode
                                                        }
                                                        className={"w-full"}
                                                    />
                                                    {/* <input
                                                        id="password"
                                                        name="password"
                                                        type="password"
                                                        autoComplete="current-password"
                                                        required
                                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                                    /> */}
                                                </div>
                                            </div>
                                        ) : (
                                            <></>
                                        )}

                                        {/* <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <input
                                                    id="remember-me"
                                                    name="remember-me"
                                                    type="checkbox"
                                                    className="h-4 w-4 text-green-700 focus:ring-indigo-500 border-gray-300 rounded"
                                                />
                                                <label
                                                    htmlFor="remember-me"
                                                    className="ml-2 block text-sm text-gray-900"
                                                >
                                                    Remember me
                                                </label>
                                            </div>
                                        </div> */}

                                        <div>
                                            <button
                                                type="submit"
                                                onClick={submitSignIn}
                                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-700 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                disabled={!validated}
                                            >
                                                {mainButtonText}
                                            </button>
                                        </div>
                                        {showAccessCodeInput ? (
                                            <div>
                                                <button
                                                    type="submit"
                                                    onClick={() => {
                                                        setShowAccessCodeInput(
                                                            false
                                                        );
                                                        setShowGuestAccessButton(
                                                            false
                                                        );
                                                        dispatch(
                                                            Actions.setLoginDetails(
                                                                {
                                                                    emailAddress:
                                                                        "",
                                                                    accessCode:
                                                                        "",
                                                                }
                                                            )
                                                        );
                                                    }}
                                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                                >
                                                    {xtx(
                                                        "Start over",
                                                        "signup.start_over"
                                                    )}
                                                </button>
                                            </div>
                                        ) : (
                                            <></>
                                        )}
                                        {showGuestAccessButton ? (
                                            <div>
                                                <button
                                                    type="submit"
                                                    onClick={() => {
                                                        setSignInAsGuest(true);
                                                    }}
                                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-700 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                >
                                                    Click here for guest access
                                                    instead
                                                </button>
                                            </div>
                                        ) : (
                                            <></>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                </div>
                <div style={{ gridArea: "clientinfo" }}>
                    <>
                        <div className="flex flex-col justify-center sm:px-6 lg:px-8">
                            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                                    {clientDisplayData.titleText}
                                </h2>
                                <h2 className="mt-6 text-center text-gray-900 text-lg">
                                    {clientDisplayData.subtitleText}
                                </h2>
                            </div>
                            {clientDisplayData.additionalInfoMd ? (
                                <MarkdownContent
                                    source={clientDisplayData.additionalInfoMd}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                    </>
                </div>
                <div style={{ gridArea: "footer" }}>
                    <Footer
                        className="fixedFooter"
                        style={{ height: "50px" }}
                        isPublic={true}
                    />
                </div>
            </div>
            {/* <SignInSignUp
                signInFn={signInFn}
                signUpFn={signUpFn}
                handleCodeSubmit={handleCodeSubmit}
                clientDisplayData={clientDisplayData}
            /> */}
        </Page>
    );
};

export default SignUp;
